import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthLayout from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { t } = useTranslation();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Se l'utente è già autenticato (sessione valida), salta /login.
  useEffect(() => {
    if (user) {
      const isPlatform = user.role === "platform_owner" || user.role === "platform_admin";
      navigate(isPlatform ? "/platform" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  // Iter 65 — Demo accounts are NEVER visible in the public production
  // login. Two-line gating: (a) hide in NODE_ENV=production (CRA replaces
  // this with the string literal "production" so terser dead-code-
  // eliminates the entire branch); (b) AND `REACT_APP_DEMO_LOGIN_ENABLED`
  // env var must explicitly be "true" — used by preview / QA sandboxes
  // that build with NODE_ENV=production but still want the quick-fill.
  const DEMO_LOGIN_ENABLED =
    process.env.NODE_ENV !== "production" ||
    process.env.REACT_APP_DEMO_LOGIN_ENABLED === "true";

  const [demoAccounts, setDemoAccounts] = useState([]);

  useEffect(() => {
    if (!DEMO_LOGIN_ENABLED) return;
    // Dynamic import keeps the credentials out of the main bundle, and
    // the helper module itself short-circuits to [] in production
    // unless the explicit flag is set.
    import("./_devDemoAccounts").then((m) => {
      setDemoAccounts(m.DEMO_ACCOUNTS || []);
    }).catch(() => { /* silently noop in prod */ });
  }, [DEMO_LOGIN_ENABLED]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      const msg = res.error || t("auth.error_login_failed");
      setError(msg);
      toast.error(msg);
      return;
    }
    toast.success(t("auth.toast_welcome"));
    // Phase 11.5 — Server-computed deterministic redirect_url has priority.
    // It is computed by `services.post_auth_redirect` and routes:
    //   - platform users → /platform
    //   - hotel tenants  → /hotel/dashboard
    //   - restaurant     → /restaurant/dashboard
    //   - suspended/cancelled → /billing
    //   - fallback       → /dashboard (NEVER the marketing "/" home page)
    // When ?from=… is present (user was bounced by ProtectedRoute) we honor
    // it instead so the user lands on the page they originally requested.
    const isPlatform =
      res.user?.role === "platform_owner" || res.user?.role === "platform_admin";
    const serverRedirect = res.redirect_url;
    const fallback = serverRedirect || (isPlatform ? "/platform" : "/dashboard");
    const redirect = location.state?.from?.pathname || fallback;
    navigate(redirect, { replace: true });
  };

  const fill = (acc) => {
    setEmail(acc.email);
    setPassword(acc.password);
  };

  return (
    <AuthLayout
      title={t("auth.welcome_back")}
      subtitle={t("auth.welcome_back_subtitle")}
      footer={
        <>
          {t("auth.new_to_app")}{" "}
          <Link
            to="/register"
            className="font-medium text-[#102A43] hover:underline"
            data-testid="link-register"
          >
            {t("auth.create_account_link")}
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="text-[#0f172a]">
            {t("auth.email")}
          </Label>
          <div className="relative mt-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.email_placeholder")}
              className="pl-9 h-11"
              data-testid="login-email"
              name="email"
              autoComplete="email"
              inputMode="email"
              spellCheck={false}
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-[#0f172a]">
              {t("auth.password")}
            </Label>
            <Link
              to="/forgot-password"
              className="text-xs text-[#102A43] hover:underline"
              data-testid="link-forgot"
            >
              {t("auth.forgot_link")}
            </Link>
          </div>
          <div className="relative mt-1">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("auth.password_placeholder")}
              className="pl-9 pr-10 h-11"
              data-testid="login-password"
              name="password"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword
                ? (t("auth.hide_password") || "Nascondi password")
                : (t("auth.show_password") || "Mostra password")}
              aria-pressed={showPassword}
              tabIndex={0}
              data-testid="login-password-toggle"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[#475569] hover:text-[#0F172A] hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#0F766E]/40 transition-colors"
            >
              {showPassword
                ? <EyeOff className="w-4 h-4" aria-hidden="true" />
                : <Eye    className="w-4 h-4" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {error && (
          <div
            className="px-3 py-2 rounded-lg bg-[#FDF0EC] text-[#B91C1C] text-sm"
            data-testid="login-error"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-11 bg-[#102A43] hover:bg-[#0b1b2c] text-white"
          data-testid="login-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.sign_in_btn")}
        </Button>

        <div className="flex items-center justify-between text-xs mt-1">
          <Link
            to="/forgot-password"
            className="text-[#102A43] hover:underline"
            data-testid="login-forgot-link"
          >
            {t("auth.forgot_link")}
          </Link>
          <Link
            to="/signup"
            className="text-[#102A43] font-medium hover:underline"
            data-testid="login-signup-link"
          >
            {t("auth.signup_link")}
          </Link>
        </div>
      </form>

      {DEMO_LOGIN_ENABLED && demoAccounts.length > 0 && (
        <div className="mt-8" data-testid="login-demo-section">
          <div className="label-eyebrow mb-3">{t("auth.demo_accounts")}</div>
          <div className="grid grid-cols-2 gap-2">
            {demoAccounts.map((acc) => (
              <button
                key={acc.email}
                type="button"
                onClick={() => fill(acc)}
                className="text-left px-3 py-2 rounded-lg border border-[#D3D7D5] hover:border-[#102A43] hover:bg-[#E0F2F1] transition-colors"
                data-testid={`demo-${acc.email.split("@")[0]}`}
              >
                <div className="text-sm font-medium text-[#0f172a]">{acc.label}</div>
                <div className="text-[11px] text-[#475569] truncate">{acc.email}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
